import React, { useEffect, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";

// Shared Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";

// Website Pages
const HomePage = lazy(() => import("./pages/HomePage.jsx"));
const AboutPage = lazy(() => import("./pages/AboutPage.jsx"));
const SchedulePage = lazy(() => import("./pages/SchedulePage.jsx"));
const ExhibitionPage = lazy(() => import("./pages/ExhibitionPage.jsx"));
const FloorPlanPage = lazy(() => import("./pages/FloorPlanPage.jsx"));
const SponsorsPage = lazy(() => import("./pages/SponsorsPage.jsx"));
const OnboardSponsorsPage = lazy(() => import("./pages/OnboardSponsorsPage.jsx"));
const SponsorshipBenefitsPage = lazy(() => import("./pages/SponsorshipBenefitsPage.jsx"));
const DelegatesPage = lazy(() => import("./pages/DelegatesPage.jsx"));
const SpeakersPage = lazy(() => import("./pages/SpeakersPage.jsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.jsx"));
const Brochure = lazy(() => import("./pages/Brochure.jsx"));
const Gris2025 = lazy(() => import("./pages/Gris2025.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx"));
const TermsConditions = lazy(() => import("./pages/TermsConditions.jsx"));

// Admin Pages
const AdminLogin = lazy(() =>
  import("./features/admin/AdminLogin.jsx")
);

const AdminDashboard = lazy(() =>
  import("./features/admin/AdminDashboard.jsx")
);

// Scroll to Top
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Loader
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0a1d37]">
    <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default function App() {
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <div className="flex flex-col min-h-screen bg-light">
      <ScrollToTop />

      {!isAdminRoute && <Header />}

      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ================= WEBSITE ================= */}

            <Route path="/" element={<HomePage />} />

            <Route path="/about" element={<AboutPage />} />

            <Route path="/schedule" element={<SchedulePage />} />

            <Route path="/exhibition" element={<ExhibitionPage />} />

            <Route path="/floor-plan" element={<FloorPlanPage />} />

            <Route path="/sponsors" element={<OnboardSponsorsPage />} />
            
            <Route path="/sponsorship-benefits" element={<SponsorshipBenefitsPage />} />
            
            <Route path="/sponsorship" element={<SponsorsPage />} />

            <Route path="/delegates" element={<DelegatesPage />} />

            <Route path="/speakers" element={<SpeakersPage />} />

            <Route path="/contact" element={<ContactPage />} />

            <Route path="/brochure" element={<Brochure />} />

            <Route path="/gris-2025" element={<Gris2025 />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            <Route path="/terms-conditions" element={<TermsConditions />} />

            {/* ================= ADMIN ================= */}

            {/* Login */}
            <Route
              path="/admin"
              element={<AdminLogin />}
            />

            {/* Dashboard */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* ================= 404 ================= */}

            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />
          </Routes>
        </Suspense>
      </main>

      {!isAdminRoute && <Footer />}
    </div>
  );
}